import type { SponsorTier } from '@rw/shared';
import { useT } from '../../i18n';
import { BrandLogo } from '../admin/BrandsTab';
import { useBrands } from '../admin/brands-api';
import { TIER_LABEL } from './event-format';

export type SponsorPick = { brandId: string; tier: SponsorTier; note: string };

const TIERS: SponsorTier[] = ['DIAMOND', 'GOLD', 'SILVER', 'PARTNER'];
const input = 'h-10 w-full rounded-xl border border-ink-700 bg-ink-850 px-3 text-sm outline-none focus:border-volt';

/** Game owner: pick an event's sponsors, each with a tier and what it sponsors. */
export function SponsorsEditor({ value, onChange }: { value: SponsorPick[]; onChange: (v: SponsorPick[]) => void }) {
  const t = useT();
  const brands = useBrands();
  const all = brands.data ?? [];
  const available = all.filter((b) => !b.archived && !value.some((v) => v.brandId === b.id));
  const set = (i: number, patch: Partial<SponsorPick>) => onChange(value.map((v, j) => (j === i ? { ...v, ...patch } : v)));
  return (
    <div data-testid="event-sponsors-editor">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-widest text-ink-400">{t('Sponsors')}</span>
      <div className="space-y-2">
        {value.map((s, i) => {
          const brand = all.find((b) => b.id === s.brandId);
          return (
            <div key={s.brandId} className="rounded-xl border border-ink-700 p-2" data-testid={`sponsor-row-${brand?.name ?? s.brandId}`}>
              <div className="flex items-center gap-2">
                {brand ? <BrandLogo brand={brand} className="h-8 w-12" /> : null}
                <span className="min-w-0 flex-1 truncate text-sm font-semibold">{brand?.name ?? '…'}</span>
                <select value={s.tier} onChange={(e) => set(i, { tier: e.target.value as SponsorTier })} className={`${input} w-36`} data-testid="sponsor-tier">
                  {TIERS.map((tier) => (
                    <option key={tier} value={tier}>
                      {t(TIER_LABEL[tier])}
                    </option>
                  ))}
                </select>
                <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="px-2 text-lg text-ink-400" aria-label={t('Remove')}>
                  ×
                </button>
              </div>
              <input
                value={s.note}
                maxLength={300}
                onChange={(e) => set(i, { note: e.target.value })}
                placeholder={t('What they sponsor (e.g. finisher shirts)')}
                className={`${input} mt-2`}
                data-testid="sponsor-note"
              />
            </div>
          );
        })}
        {available.length ? (
          <select
            value=""
            onChange={(e) => e.target.value && onChange([...value, { brandId: e.target.value, tier: 'PARTNER', note: '' }])}
            className={input}
            data-testid="sponsor-add"
          >
            <option value="">{t('+ Add a sponsor')}</option>
            {available.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        ) : all.length === 0 ? (
          <p className="text-xs text-ink-500">{t('Add brands in Owner → Brands first.')}</p>
        ) : null}
      </div>
    </div>
  );
}
